/**
 * SandboxConfigsPage — Dedicated page for managing sandbox configurations.
 *
 * Sandbox configurations are project-level resources, so they have their own sidebar
 * nav item and route rather than living under the Overview resource tabs.
 */

import { useState } from 'react';
import { makeStyles } from '@coreai/fluentui-react';
import { SandboxConfigList } from '@/components/SandboxList/SandboxConfigList';
import { CreateOrEditSandboxConfigDialog } from '@/components/SandboxList/CreateOrEditSandboxConfigDialog';
import type { SandboxConfiguration } from '@/types/sandbox';
import { useAppsInProject } from '@/hooks/useApps';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { PageSection } from '@/components/shared/PageSection';
import { ProjectOverviewHeader } from '@/components/shared/ProjectOverviewHeader';

const useStyles = makeStyles({
  page: {
    // Outer padding + horizontal layout come from `ProjectLayout.mainContent`.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
});

export const SandboxConfigsPage = () => {
  const styles = useStyles();
  const { projectName, project } = useCurrentProject();
  const { data: apps } = useAppsInProject(projectName ?? '');
  useDocumentTitle(['Sandboxes', projectName]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editSandboxConfig, setEditSandboxConfig] = useState<SandboxConfiguration | null>(null);
  const showEditDialog = !!editSandboxConfig;

  return (
    <div className={styles.page}>
      {projectName && (
        <ProjectOverviewHeader
          name={projectName}
          project={project}
          appCount={apps?.length ?? 0}
        />
      )}

      <PageSection title="Sandboxes">
        <div className={styles.list}>
          <SandboxConfigList
            onCreateSandboxConfig={() => setShowCreateDialog(true)}
            onEditSandboxConfig={(sandboxConfig) => setEditSandboxConfig(sandboxConfig)}
          />
        </div>
      </PageSection>

      <CreateOrEditSandboxConfigDialog
        open={showCreateDialog}
        onCancel={() => setShowCreateDialog(false)}
      />

      {editSandboxConfig && (
        <CreateOrEditSandboxConfigDialog
          open={showEditDialog}
          onCancel={() => setEditSandboxConfig(null)}
          sandboxConfig={editSandboxConfig}
        />
      )}
    </div>
  );
};
